// MARGARET NÚÑEZ - PERSONAL BRAND WEBSITE CONTENT
// Brand positioning: Founder / Creative Director
// Bilingual: English / Spanish

export const siteContent = {
  en: {
    brand: {
      name: 'Margaret Núñez',
      tagline: 'Founder / Creative Director',
      coreStatement: 'Creating experiences that people remember—on screen and in real life.',
    },
    nav: {
      home: 'Home',
      evoke: 'Evoke',
      ventures: 'Ventures',
      shop: 'Studio',
      blog: 'Blog',
      contact: 'Contact',
      partnerWithMe: 'Partner With Me',
    },
    home: {
      hero: {
        headline: 'Experiences That People Remember',
        subheadline: 'On screen and in real life. Rooted in hospitality, taste, intention, and operational excellence.',
        cta1: 'Partner With Me',
        cta2: 'View Work',
      },
      lanes: [
        {
          title: 'Brand Presence',
          subtitle: 'Digital Strategy',
          overline: 'DIGITAL STRATEGY',
          headline: 'Launch & Campaign Strategy',
          description: 'Brand moments designed to live beyond the room. From narrative to rollout—so the moment becomes movement.',
          capabilities: ['Launch Strategy + Rollout Plan', 'Campaign Concepts + Creative Direction', 'Content Architecture (Pre / During / Post)', 'Partnerships + Guest Strategy'],
        },
        {
          title: 'Live Experiences',
          subtitle: 'In Person',
          overline: 'IN PERSON',
          headline: 'Live Experiences',
          description: 'Hospitality-driven events. Curated gatherings. Designed for impact in the room—and assets after the moment.',
          capabilities: ['Event Design', 'Culinary Experiences', 'Brand Activations', 'Executive Gatherings'],
        },
        {
          title: 'Operating Systems',
          subtitle: 'Behind the Scenes',
          overline: 'BEHIND THE SCENES',
          headline: 'Operating Systems',
          description: 'Tech-enabled operations. Systems that scale. Dashboards and workflows built from real execution.',
          capabilities: ['Platform Development', 'Operational Systems', 'Team Infrastructure', 'Process Design'],
        },
      ],
      manifesto: {
        title: 'Words We Live By',
        lines: [
          'Every detail tells a story.',
          'Hospitality is an operating system.',
          'Taste is a business advantage.',
          'Intention beats perfection.',
          'Systems enable creativity.',
          'Excellence is a daily practice.',
          'Experiences compound.',
        ],
      },
    },
    evoke: {
      hero: {
        headline: 'EVOKE',
        subheadline: 'Designing experiences with intention - on screen and in real life.',
        description: 'I build brand moments the way I build events: with narrative, taste, flow, and execution. Because a moment is not the goal. Movement is.',
        cta1: 'Partner With Me',
        cta2: 'Explore the Method',
      },
      whatIs: {
        title: 'What Evoke Is',
        paragraphs: [
          'Evoke is my signature approach. Not a trend. Not a template.',
          'A framework for creating experiences that feel premium - everywhere your brand shows up.',
          'Evoke connects: launches and campaign moments, live hospitality experiences, and operating systems built from real execution.',
        ],
      },
      wordsLiveBy: {
        title: 'Words I Live By',
        lines: [
          'Intention over noise.',
          'Taste is a strategy.',
          'Luxury is consistency.',
          'Details are the difference.',
          'Execution is the brand.',
          'The room matters. So does the rollout.',
        ],
      },
      pillars: {
        title: 'The Evoke Pillars',
        items: [
          { title: 'Narrative', desc: 'What the moment means.' },
          { title: 'Visual Direction', desc: 'How it looks everywhere.' },
          { title: 'Sensory Detail', desc: 'How it feels.' },
          { title: 'Flow', desc: 'How it moves.' },
          { title: 'Execution', desc: 'How it lands.' },
        ],
      },
      proof: {
        title: 'Selected Proof',
        subtitle: 'A few moments. Enough to understand the standard.',
        items: [
          { title: 'Brand Moments', desc: 'Launches, activations, and campaign direction.' },
          { title: 'Live Experiences', desc: 'Hospitality-driven execution and interactive stations.' },
          { title: 'Operating Systems', desc: 'Workflows that protect quality, margin, and handoffs.' },
        ],
        cta: 'Request Credentials',
      },
      about: {
        title: 'About Margaret',
        lines: [
          'Founder / Creative Director.',
          'Built in hospitality, where execution is the standard.',
          'I design moments that travel across the room and across digital.',
        ],
        cta: 'Read the full story',
      },
      partners: {
        title: 'Ways to Partner',
        items: [
          { title: 'Brand Partnerships', path: '/contact' },
          { title: 'Ciano Gourmet (DR)', path: '/ventures/ciano-gourmet' },
          { title: 'Ciano Bites (US)', path: '/ventures/ciano-bites' },
          { title: 'StaffDO', path: '/ventures/staffdo' },
          { title: 'Kora OS', path: '/ventures/kora-os' },
        ],
      },
      cta: {
        headline: 'If your brand is building a moment - build it on purpose.',
        response: 'Response within 24-48 hours',
        button: 'Partner With Me',
      },
    },
    about: {
      hero: {
        headline: 'About Margaret',
        subheadline: 'Your partner in creating unforgettable, sensory-driven event experiences',
      },
      story: {
        title: 'The Story Behind the Design',
        content: 'With over a decade of experience in event design and production, Margaret Núñez has transformed countless visions into extraordinary realities. Her passion for creating immersive, sensory-driven experiences has made her a sought-after designer in the hospitality and events industry.',
      },
      philosophy: {
        title: 'Our Philosophy',
        content: 'We believe that every event should be a masterpiece—a carefully orchestrated symphony of sight, sound, taste, touch, and scent. Our approach goes beyond traditional event planning to create truly immersive experiences that resonate with your guests long after the event ends.',
      },
      apart: {
        title: 'What Sets Us Apart',
        items: [
          'Sensory-driven design that engages all five senses',
          'Meticulous attention to every detail, no matter how small',
          'Innovative approach combining hospitality and tech-enabled platforms',
          'Personalized service tailored to your unique vision',
        ],
      },
      values: {
        title: 'Our Core Values',
        items: [
          { title: 'Excellence', desc: 'We pursue perfection in every detail, ensuring your event exceeds expectations.' },
          { title: 'Innovation', desc: 'We constantly push boundaries to create fresh, memorable experiences.' },
          { title: 'Authenticity', desc: 'We create genuine experiences that truly reflect your vision and values.' },
        ],
      },
      cta: {
        title: 'Ready to Work Together?',
        content: "Let's create something extraordinary. Schedule a consultation to discuss your vision and discover how we can bring it to life.",
        button: 'Schedule a Consultation',
      },
    },
    contact: {
      hero: {
        headline: "Let's Work Together",
        subheadline: 'Collaborations, partnerships, and client projects.',
      },
      form: {
        name: 'Your Name',
        email: 'Email',
        company: 'Company/Organization',
        projectType: 'Project Type',
        projectOptions: ['Event/Experience', 'Catering', 'Brand Collaboration', 'Operational Consulting', 'Partnership Opportunity', 'Other'],
        description: 'Tell me about your project',
        timeline: 'Timeline',
        timelinePlaceholder: 'When do you need this?',
        budget: 'Budget Range (optional)',
        submit: 'Send Message',
      },
      responseTime: 'We respond to all inquiries within 48 hours.',
    },
    shop: {
      hero: {
        headline: 'Studio',
        subheadline: 'Systems, templates, and playbooks to elevate your operations and brand presence.',
      },
      collections: {
        title: 'Studio Collections',
        items: [
          {
            title: 'Event Concepts',
            description: 'Curated event concepts designed to be remixed—and executed beautifully.',
          },
          {
            title: 'Launch Blueprints',
            description: 'From narrative to rollout: structure for premium launches and campaign moments.',
          },
          {
            title: 'Operating Systems',
            description: 'Workflows, dashboards, and tools that protect margin, handoffs, and consistency.',
          },
          {
            title: 'Templates & SOPs',
            description: 'The essentials: briefs, checklists, and repeatable execution tools.',
          },
        ],
      },
      categories: {
        experienceDesign: 'Experience Design Kits',
        brandPresence: 'Brand Presence Playbooks',
        founderSystems: 'Founder Systems & Templates',
      },
      productLabels: {
        outcome: 'Outcome',
        whatsInside: "What's Inside",
        whoItsFor: 'Who it\'s for',
        format: 'Format',
        addToCart: 'Add to Cart',
      },
    },
    blog: {
      hero: {
        headline: 'The Journal',
        subheadline: 'Insights on experience design, founder systems, and brand presence.',
      },
      readMore: 'Read More',
      allPosts: 'All Posts',
    },
    features: {
      hero: {
        headline: 'FEATURES',
        subheadline: 'Watch. Read. Learn.',
        description: 'Insights, stories, and lessons from a decade of building experiences, brands, and systems.',
      },
      watch: 'Watch',
      read: 'Read',
      newsletter: {
        title: 'Get insights delivered',
        description: 'Join the newsletter for weekly thoughts on experience design, brand building, and founder systems.',
        placeholder: 'Enter your email',
        button: 'Subscribe',
      },
      cta: {
        title: 'Ready to create something together?',
        button: 'Partner With Me',
      },
    },
    press: {
      hero: {
        headline: 'PRESS',
        subheadline: 'Media features, interviews, and press inquiries.',
      },
      pressKit: {
        title: 'Press Kit',
        description: 'Download high-resolution photos, brand assets, and bio for media use.',
        button: 'Download Press Kit',
      },
      featuredIn: 'Featured In',
      contact: {
        title: 'Press Inquiries',
        description: 'For interviews, speaking engagements, or media requests, please reach out directly.',
        button: 'Contact for Press',
      },
    },
    policies: {
      hero: {
        headline: 'POLICIES',
        subheadline: 'Terms of service, privacy policy, and other legal information.',
      },
      privacy: {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: January 2025',
      },
      terms: {
        title: 'Terms of Service',
      },
      refund: {
        title: 'Refund Policy',
      },
    },
    footer: {
      followInstagram: 'Follow on Instagram',
      links: {
        about: 'About',
        mediaKit: 'Media Kit',
        press: 'Press',
        policies: 'Policies',
      },
      copyright: '© 2025 Margaret Núñez. All rights reserved.',
    },
  },
  es: {
    brand: {
      name: 'Margaret Núñez',
      tagline: 'Fundadora / Directora Creativa',
      coreStatement: 'Creando experiencias que la gente recuerda—en pantalla y en la vida real.',
    },
    nav: {
      home: 'Inicio',
      evoke: 'Evoke',
      ventures: 'Ventures',
      shop: 'Studio',
      blog: 'Blog',
      contact: 'Contacto',
      partnerWithMe: 'Colaboremos',
    },
    home: {
      hero: {
        headline: 'Experiencias Que La Gente Recuerda',
        subheadline: 'En pantalla y en la vida real. Arraigadas en hospitalidad, buen gusto, intención y excelencia operativa.',
        cta1: 'Colaboremos',
        cta2: 'Ver Trabajo',
      },
      lanes: [
        {
          title: 'Presencia de Marca',
          subtitle: 'Estrategia Digital',
          overline: 'ESTRATEGIA DIGITAL',
          headline: 'Estrategia de Lanzamiento y Campaña',
          description: 'Momentos de marca diseñados para vivir más allá del momento. De la narrativa al despliegue—para que el momento se convierta en movimiento.',
          capabilities: ['Estrategia de Lanzamiento + Plan de Despliegue', 'Conceptos de Campaña + Dirección Creativa', 'Arquitectura de Contenido (Antes / Durante / Después)', 'Alianzas + Estrategia de Invitados'],
        },
        {
          title: 'Experiencias en Vivo',
          subtitle: 'En Persona',
          overline: 'EN PERSONA',
          headline: 'Experiencias en Vivo',
          description: 'Eventos impulsados por la hospitalidad. Reuniones curadas. Diseñadas para impactar en el momento—y crear activos después.',
          capabilities: ['Diseño de Eventos', 'Experiencias Culinarias', 'Activaciones de Marca', 'Reuniones Ejecutivas'],
        },
        {
          title: 'Sistemas Operativos',
          subtitle: 'Detrás de Escena',
          overline: 'DETRÁS DE ESCENA',
          headline: 'Sistemas Operativos',
          description: 'Operaciones habilitadas por tecnología. Sistemas que escalan. Dashboards y flujos de trabajo construidos desde la ejecución real.',
          capabilities: ['Desarrollo de Plataformas', 'Sistemas Operativos', 'Infraestructura de Equipo', 'Diseño de Procesos'],
        },
      ],
      manifesto: {
        title: 'Palabras Por Las Que Vivimos',
        lines: [
          'Cada detalle cuenta una historia.',
          'La hospitalidad es un sistema operativo.',
          'El buen gusto es una ventaja empresarial.',
          'La intención supera la perfección.',
          'Los sistemas habilitan la creatividad.',
          'La excelencia es una práctica diaria.',
          'Las experiencias se acumulan.',
        ],
      },
    },
    evoke: {
      hero: {
        headline: 'EVOKE',
        subheadline: 'Diseñando experiencias con intención - en pantalla y en la vida real.',
        description: 'Construyo momentos de marca como construyo eventos: con narrativa, buen gusto, flujo y ejecución. Porque un momento no es la meta. El movimiento sí.',
        cta1: 'Colaboremos',
        cta2: 'Explora el Método',
      },
      whatIs: {
        title: '¿Qué es Evoke?',
        paragraphs: [
          'Evoke es mi enfoque distintivo. No es una tendencia. No es una plantilla.',
          'Un marco para crear experiencias que se sienten premium—donde sea que tu marca aparezca.',
          'Evoke conecta: lanzamientos y momentos de campaña, experiencias de hospitalidad en vivo, y sistemas operativos construidos desde la ejecución real.',
        ],
      },
      wordsLiveBy: {
        title: 'Palabras Por Las Que Vivo',
        lines: [
          'Intención sobre ruido.',
          'El buen gusto es una estrategia.',
          'El lujo es consistencia.',
          'Los detalles hacen la diferencia.',
          'La ejecución es la marca.',
          'El espacio importa. También el despliegue.',
        ],
      },
      pillars: {
        title: 'Los Pilares de Evoke',
        items: [
          { title: 'Narrativa', desc: 'Lo que significa el momento.' },
          { title: 'Dirección Visual', desc: 'Cómo se ve en todas partes.' },
          { title: 'Detalle Sensorial', desc: 'Cómo se siente.' },
          { title: 'Flujo', desc: 'Cómo se mueve.' },
          { title: 'Ejecución', desc: 'Cómo aterriza.' },
        ],
      },
      proof: {
        title: 'Prueba Seleccionada',
        subtitle: 'Algunos momentos. Suficientes para entender el estándar.',
        items: [
          { title: 'Momentos de Marca', desc: 'Lanzamientos, activaciones y dirección de campaña.' },
          { title: 'Experiencias en Vivo', desc: 'Ejecución impulsada por hospitalidad y estaciones interactivas.' },
          { title: 'Sistemas Operativos', desc: 'Flujos que protegen calidad, margen y traspasos.' },
        ],
        cta: 'Solicitar Credenciales',
      },
      about: {
        title: 'Sobre Margaret',
        lines: [
          'Fundadora / Directora Creativa.',
          'Formada en hospitalidad, donde la ejecución es el estándar.',
          'Diseño momentos que viajan por el espacio y por lo digital.',
        ],
        cta: 'Lee la historia completa',
      },
      partners: {
        title: 'Formas de Colaborar',
        items: [
          { title: 'Alianzas de Marca', path: '/contact' },
          { title: 'Ciano Gourmet (RD)', path: '/ventures/ciano-gourmet' },
          { title: 'Ciano Bites (US)', path: '/ventures/ciano-bites' },
          { title: 'StaffDO', path: '/ventures/staffdo' },
          { title: 'Kora OS', path: '/ventures/kora-os' },
        ],
      },
      cta: {
        headline: 'Si tu marca está construyendo un momento - constrúyelo con propósito.',
        response: 'Respuesta en 24-48 horas',
        button: 'Colaboremos',
      },
    },
    about: {
      hero: {
        headline: 'Sobre Margaret',
        subheadline: 'Tu socia en la creación de experiencias inolvidables impulsadas por los sentidos',
      },
      story: {
        title: 'La Historia Detrás del Diseño',
        content: 'Con más de una década de experiencia en diseño y producción de eventos, Margaret Núñez ha transformado innumerables visiones en realidades extraordinarias. Su pasión por crear experiencias inmersivas impulsadas por los sentidos la ha convertido en una diseñadora muy solicitada en la industria de hospitalidad y eventos.',
      },
      philosophy: {
        title: 'Nuestra Filosofía',
        content: 'Creemos que cada evento debe ser una obra maestra—una sinfonía cuidadosamente orquestada de vista, sonido, gusto, tacto y olfato. Nuestro enfoque va más allá de la planificación tradicional de eventos para crear experiencias verdaderamente inmersivas que resuenan con tus invitados mucho después de que el evento termine.',
      },
      apart: {
        title: 'Lo Que Nos Distingue',
        items: [
          'Diseño sensorial que involucra los cinco sentidos',
          'Atención meticulosa a cada detalle, sin importar cuán pequeño',
          'Enfoque innovador que combina hospitalidad y plataformas tecnológicas',
          'Servicio personalizado adaptado a tu visión única',
        ],
      },
      values: {
        title: 'Nuestros Valores Fundamentales',
        items: [
          { title: 'Excelencia', desc: 'Perseguimos la perfección en cada detalle, asegurando que tu evento supere las expectativas.' },
          { title: 'Innovación', desc: 'Constantemente empujamos los límites para crear experiencias frescas y memorables.' },
          { title: 'Autenticidad', desc: 'Creamos experiencias genuinas que reflejan verdaderamente tu visión y valores.' },
        ],
      },
      cta: {
        title: '¿Lista para Trabajar Juntas?',
        content: 'Creemos algo extraordinario. Agenda una consulta para discutir tu visión y descubrir cómo podemos darle vida.',
        button: 'Agendar Consulta',
      },
    },
    contact: {
      hero: {
        headline: 'Trabajemos Juntos',
        subheadline: 'Colaboraciones, alianzas y proyectos de clientes.',
      },
      form: {
        name: 'Tu Nombre',
        email: 'Correo Electrónico',
        company: 'Empresa/Organización',
        projectType: 'Tipo de Proyecto',
        projectOptions: ['Evento/Experiencia', 'Catering', 'Colaboración de Marca', 'Consultoría Operativa', 'Oportunidad de Alianza', 'Otro'],
        description: 'Cuéntame sobre tu proyecto',
        timeline: 'Cronograma',
        timelinePlaceholder: '¿Cuándo lo necesitas?',
        budget: 'Rango de Presupuesto (opcional)',
        submit: 'Enviar Mensaje',
      },
      responseTime: 'Respondemos a todas las consultas en 48 horas.',
    },
    shop: {
      hero: {
        headline: 'Studio',
        subheadline: 'Sistemas, plantillas y playbooks para elevar tus operaciones y presencia de marca.',
      },
      collections: {
        title: 'Colecciones del Studio',
        items: [
          {
            title: 'Conceptos de Eventos',
            description: 'Conceptos de eventos curados, diseñados para ser remezclados—y ejecutados bellamente.',
          },
          {
            title: 'Blueprints de Lanzamiento',
            description: 'De la narrativa al despliegue: estructura para lanzamientos premium y momentos de campaña.',
          },
          {
            title: 'Sistemas Operativos',
            description: 'Flujos de trabajo, dashboards y herramientas que protegen margen, traspasos y consistencia.',
          },
          {
            title: 'Plantillas y SOPs',
            description: 'Los esenciales: briefs, checklists y herramientas de ejecución repetibles.',
          },
        ],
      },
      categories: {
        experienceDesign: 'Kits de Diseño de Experiencias',
        brandPresence: 'Playbooks de Presencia de Marca',
        founderSystems: 'Sistemas y Plantillas para Fundadores',
      },
      productLabels: {
        outcome: 'Resultado',
        whatsInside: 'Qué Incluye',
        whoItsFor: 'Para Quién Es',
        format: 'Formato',
        addToCart: 'Agregar al Carrito',
      },
    },
    blog: {
      hero: {
        headline: 'El Diario',
        subheadline: 'Perspectivas sobre diseño de experiencias, sistemas para fundadores y presencia de marca.',
      },
      readMore: 'Leer Más',
      allPosts: 'Todos los Posts',
    },
    features: {
      hero: {
        headline: 'CONTENIDO',
        subheadline: 'Mira. Lee. Aprende.',
        description: 'Perspectivas, historias y lecciones de una década construyendo experiencias, marcas y sistemas.',
      },
      watch: 'Mira',
      read: 'Lee',
      newsletter: {
        title: 'Recibe perspectivas directo a tu correo',
        description: 'Únete al newsletter para pensamientos semanales sobre diseño de experiencias, construcción de marca y sistemas para fundadores.',
        placeholder: 'Ingresa tu correo',
        button: 'Suscribirse',
      },
      cta: {
        title: '¿Lista para crear algo juntas?',
        button: 'Colaboremos',
      },
    },
    press: {
      hero: {
        headline: 'PRENSA',
        subheadline: 'Apariciones en medios, entrevistas y consultas de prensa.',
      },
      pressKit: {
        title: 'Kit de Prensa',
        description: 'Descarga fotos en alta resolución, activos de marca y biografía para uso en medios.',
        button: 'Descargar Kit de Prensa',
      },
      featuredIn: 'Apariciones En',
      contact: {
        title: 'Consultas de Prensa',
        description: 'Para entrevistas, charlas o solicitudes de medios, contáctanos directamente.',
        button: 'Contacto de Prensa',
      },
    },
    policies: {
      hero: {
        headline: 'POLÍTICAS',
        subheadline: 'Términos de servicio, política de privacidad y otra información legal.',
      },
      privacy: {
        title: 'Política de Privacidad',
        lastUpdated: 'Última actualización: Enero 2025',
      },
      terms: {
        title: 'Términos de Servicio',
      },
      refund: {
        title: 'Política de Reembolso',
      },
    },
    footer: {
      followInstagram: 'Sígueme en Instagram',
      links: {
        about: 'Sobre Mí',
        mediaKit: 'Media Kit',
        press: 'Prensa',
        policies: 'Políticas',
      },
      copyright: '© 2025 Margaret Núñez. Todos los derechos reservados.',
    },
  },
};

// Helper function to get content by language
export const getContent = (language = 'en') => {
  return siteContent[language] || siteContent.en;
};

export default siteContent;
